import { useCallback, useEffect, useState } from "react";
import { Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useApi, errMsg, ymd } from "@/lib/api";
import { inputCls, primaryBtn } from "@/pages/admin/AdminServices";

export default function AdminBlocks() {
  const api = useApi();
  const today = ymd(new Date());
  const [items, setItems] = useState(null);
  const [f, setF] = useState({ date_from: today, date_to: today, all_day: true, start: "14:00", end: "15:00", reason: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api("get", "/admin/blocks").then(setItems).catch((e) => toast.error(errMsg(e))), [api]);
  useEffect(() => {
    load();
  }, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (f.date_to < f.date_from) return toast.error("La fecha final debe ser posterior a la inicial");
    if (!f.all_day && f.end <= f.start) return toast.error("La hora final debe ser posterior a la inicial");
    setBusy(true);
    try {
      const r = await api("post", "/admin/blocks", f);
      toast.success(r.affected_bookings ? `Bloqueo creado. Atención: hay ${r.affected_bookings} cita(s) activa(s) en esas fechas.` : "Bloqueo creado");
      load();
    } catch (x) { toast.error(errMsg(x)); } finally { setBusy(false); }
  };
  const del = async (b) => {
    try { await api("delete", `/admin/blocks/${b.id}`); toast.success("Bloqueo eliminado"); load(); } catch (x) { toast.error(errMsg(x)); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10" data-testid="admin-blocks">
      <form onSubmit={create} className="lg:col-span-5 space-y-4 border border-zinc-200 p-6">
        <p className="font-semibold">Bloquear horario, festivo o vacaciones</p>
        <div className="grid grid-cols-2 gap-3">
          <label className="text-xs text-zinc-500">Desde<input type="date" className={`${inputCls} w-full mt-1`} value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value, date_to: e.target.value > f.date_to ? e.target.value : f.date_to })} data-testid="admin-block-from" /></label>
          <label className="text-xs text-zinc-500">Hasta<input type="date" className={`${inputCls} w-full mt-1`} value={f.date_to} min={f.date_from} onChange={(e) => setF({ ...f, date_to: e.target.value })} data-testid="admin-block-to" /></label>
        </div>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" className="accent-[#111111]" checked={f.all_day} onChange={(e) => setF({ ...f, all_day: e.target.checked })} data-testid="admin-block-allday" /> Día completo</label>
        {!f.all_day && (
          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs text-zinc-500">Hora inicio<input type="time" step="900" className={`${inputCls} w-full mt-1`} value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} data-testid="admin-block-start" /></label>
            <label className="text-xs text-zinc-500">Hora fin<input type="time" step="900" className={`${inputCls} w-full mt-1`} value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} data-testid="admin-block-end" /></label>
          </div>
        )}
        <input placeholder="Motivo (opcional): festivo, vacaciones…" className={`${inputCls} w-full`} value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="admin-block-reason" />
        <button disabled={busy} className={primaryBtn} data-testid="admin-block-submit">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Añadir bloqueo</button>
      </form>
      <div className="lg:col-span-7">
        {!items ? <Loader2 className="w-5 h-5 animate-spin text-zinc-400" /> : items.length === 0 ? <p className="text-sm text-zinc-400" data-testid="admin-no-blocks">No hay bloqueos.</p> : (
          <ul className="divide-y divide-zinc-200 border-y border-zinc-200">
            {items.map((b) => (
              <li key={b.id} className="py-4 flex justify-between items-center gap-3" data-testid={`admin-block-${b.id}`}>
                <div>
                  <p className="font-mono text-sm">{b.date_from}{b.date_to !== b.date_from && ` → ${b.date_to}`}</p>
                  <p className="text-sm text-zinc-500">{b.all_day ? "Día completo" : `${b.start} – ${b.end}`}{b.reason && ` · ${b.reason}`}</p>
                </div>
                <button onClick={() => del(b)} className="w-10 h-10 border border-zinc-300 flex items-center justify-center hover:border-red-500" aria-label="Eliminar" data-testid={`admin-block-delete-${b.id}`}><Trash2 className="w-4 h-4" /></button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
